// Launchpad entity registry: routes, queries, open handlers; processes use `processes/launchpadHome`.

import {
  ApartmentOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  BarChartOutlined,
  RobotOutlined,
  ApiOutlined,
  DatabaseOutlined,
  DesktopOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import _ from 'lodash';
import { history } from 'umi';

import CoreQueries from '@/utils/input/queries';
import { CoreEnums } from '@/utils/domain/constants';
import { connectionTypeIcons } from '@/pages/connections/connections/utils/constants';

const { ManageProcess, ManageLists, ManageReports, ManageEntities } = CoreQueries;
const ManageConnections = CoreQueries.connections || {};

const connectionTypeKeyByValue = _.invert(CoreEnums.ConnectionType || {});

const getConnectionIconNode = (raw) => {
  if (raw?.type == null) return null;
  const key = connectionTypeKeyByValue[raw.type];
  if (!key) return null;
  return connectionTypeIcons[key] || null;
};

const getConnectionMeta = (raw) => {
  const key = raw?.type != null ? connectionTypeKeyByValue[raw.type] : null;
  return {
    connectionTypeKey: key || null,
    connectionTypeLabel: key ? `connections.type.${String(key).toLowerCase()}` : null,
    isPublic: raw?.isPublic === true,
    validated: raw?.validated === true,
    // `canManage` is the per-record flag returned by `/connection/manage`
    // (admins + collaborators). The Launchpad uses it to gate the
    // configuration commands on each tile. See UX guide
    // "AccessControl vs Collaborator".
    canManage: raw?.canManage === true,
  };
};

const PROCESS_START_MODE_LOCALE_KEYS = {
  1: 'processes.select.instant',
  2: 'processes.select.automated',
  3: 'processes.select.scheduled',
};
const getProcessMeta = (raw) => {
  const startMode = raw?.startMode ?? null;
  return {
    isPublicProcess: raw?.isPublicProcess === true,
    startModeLabel: startMode != null ? PROCESS_START_MODE_LOCALE_KEYS[startMode] || null : null,
    // `canManage` is the per-record flag returned by `/process/manage`
    // (admins + collaborators). The Launchpad uses it to gate the
    // configuration commands on each tile. See UX guide
    // "AccessControl vs Collaborator".
    canManage: raw?.canManage === true,
  };
};

const getAgentMeta = (raw) => ({
  isPublic: raw?.isPublic === true,
  // Administrative team grouping (AIAgentTeam). The tile renders a
  // chip when `aiAgentTeamName` is set; the id is kept around in
  // case a parent wants to filter by team in the future.
  aiAgentTeamId: raw?.aiAgentTeamId ?? null,
  aiAgentTeamName: raw?.aiAgentTeam?.name || null,
  // `canManage` is the per-record flag returned by `/ai/agent/allowed`
  // that tells the UI whether the current user is allowed to open the
  // configuration UI (admins + collaborators). See UX guide
  // "AccessControl vs Collaborator".
  canManage: raw?.canManage === true,
});

// AISkillGroup is the skill-side analogue of AIAgentTeam (folder-style
// catalog grouping). Same shape so the Launchpad tile can render a
// generic "group chip" without per-tab branching downstream.
const getSkillMeta = (raw) => ({
  aiSkillGroupId: raw?.aiSkillGroupId ?? null,
  aiSkillGroupName: raw?.aiSkillGroup?.name || null,
  published: raw?.published === true,
  isManaged: raw?.isManaged === true,
});

const RPA_PLATFORM_LOCALE_KEYS = {
  1: 'agents.rpa.platform.windows',
  2: 'agents.rpa.platform.android',
};
const RPA_STATUS_LOCALE_KEYS = {
  0: 'agents.rpa.status.unknown',
  1: 'agents.rpa.status.online',
  2: 'agents.rpa.status.offline',
  3: 'agents.rpa.status.disabled',
};
const getRpaMeta = (raw) => {
  const rpaPlatformId = raw?.agentPlatform ?? null;
  const rpaStatusId = raw?.status ?? null;
  return {
    rpaPlatformId,
    rpaPlatformLabel:
      rpaPlatformId != null ? RPA_PLATFORM_LOCALE_KEYS[rpaPlatformId] || null : null,
    rpaStatusId,
    rpaStatusLabel: rpaStatusId != null ? RPA_STATUS_LOCALE_KEYS[rpaStatusId] || null : null,
  };
};

const getAppMeta = (raw) => ({
  // `canManage` is the per-record flag returned by `/app/manage`
  // (admins + collaborators). It is the single source of truth for
  // configuration affordances on the tile — the legacy
  // `isCurrentUserCollaborator` flag was retired.
  canManage: raw?.canManage === true,
});

const REPORT_TYPE_LOCALE_KEYS = {
  1: 'reports.type.administration',
  2: 'reports.type.process',
  3: 'reports.type.request',
  4: 'reports.type.generic',
};
const getReportMeta = (raw) => {
  const reportTypeId = raw?.type ?? null;
  return {
    reportTypeId,
    reportTypeLabel: reportTypeId != null ? REPORT_TYPE_LOCALE_KEYS[reportTypeId] || null : null,
    isPublic: raw?.isPublic === true,
    // `canManage` is the per-record flag returned by `/report/manage`
    // (admins + collaborators). The Launchpad uses it to gate the
    // configuration commands on each tile. See UX guide
    // "AccessControl vs Collaborator".
    canManage: raw?.canManage === true,
  };
};

const LIST_TYPE_LOCALE_KEYS = {
  1: 'customList.select.list',
  2: 'customList.select.gantt',
  3: 'customList.select.calendar',
  4: 'customList.select.tasklist',
  5: 'customList.select.taskBoard',
  6: 'customList.select.cardsList',
  7: 'customList.select.mapList',
};
const getListMeta = (raw) => {
  const listTypeId = raw?.type ?? null;
  return {
    listTypeId,
    listTypeLabel: listTypeId != null ? LIST_TYPE_LOCALE_KEYS[listTypeId] || null : null,
    isPublic: raw?.isPublic === true,
    // `canManage` is the per-record flag returned by `/list/allowed`
    // (admins + collaborators). The Launchpad uses it to gate the
    // "Configurar" action on each tile. See UX guide.
    canManage: raw?.canManage === true,
  };
};

const openInSameTabOrNew = (href, e) => {
  if (e && (e.metaKey || e.ctrlKey || e.button === 1)) {
    window.open(window.location.origin + href, '_blank', 'noreferrer');
    return;
  }
  history.push(href);
};

const getAppIconUrl = (app) => {
  const raw = app?.definition?.personalization?.icon;
  if (!raw) return null;
  return raw.documentTemporaryUrl ?? raw.downloadUrl ?? raw.documentUrl ?? null;
};

export const ENTITY_TYPES = {
  processes: {
    id: 'processes',
    labelKey: 'launchpad.type.processes',
    labelSingularKey: 'launchpad.type.processes.singular',
    accent: '#0051FF',
    Icon: ApartmentOutlined,
    source: 'processes',
    hasGroups: true,
    hasSubgroups: true,
    hasFavorites: true,
    nameField: 'name',
    groupIdField: 'processGroupId',
    embeddedGroupField: 'processGroup',
    groupParentField: 'parentProcessGroupId',
    // Per the "AccessControl vs Collaborator" UX guide, the Launchpad
    // is the main management view (left-sidebar `/processes` entry) →
    // uses `/process/manage`. Every other context (selectors, embedded
    // pickers, action configs) keeps reading `/process/allowed`. The
    // per-item `canManage` flag tells the UI which records are
    // configurable downstream.
    //
    // `process/groups` is the access-aware group endpoint used by both
    // regular users and admins. No `adminGroups` fallback to the legacy
    // CRUD route `processGroup/paged` — that endpoint stays reserved
    // for the administrative groups screen.
    routes: {
      allowed: 'process/manage',
      groups: 'process/groups',
      favorites: 'process/favorites',
    },
    queries: {
      // `canManage` gates configuration UI on each tile.
      allowed: {
        ...ManageProcess.allowed,
        select: `${ManageProcess.allowed.select},canManage`,
      },
      groups: ManageProcess.group,
      favorites: ManageProcess.favorite,
    },
    open: (item, e) => openInSameTabOrNew(`/processes/start?ProcessId=${item.id}`, e),
    getIconUrl: () => null,
    getMeta: getProcessMeta,
  },

  lists: {
    id: 'lists',
    labelKey: 'launchpad.type.lists',
    labelSingularKey: 'launchpad.type.lists.singular',
    accent: '#8B5CF6',
    Icon: UnorderedListOutlined,
    source: 'launchpad',
    hasGroups: true,
    hasSubgroups: false,
    hasFavorites: true,
    nameField: 'name',
    groupIdField: 'customListGroupId',
    embeddedGroupField: 'customListGroup',
    groupParentField: null,
    // Per the "AccessControl vs Collaborator" UX guide, the Launchpad
    // is the main management view (left-sidebar `/lists` entry) → uses
    // `/list/manage`. Every other context (selectors, byList legacy,
    // embedded pickers) keeps reading `/list/allowed`. The backend
    // returns the full set for admins and filters by access for regular
    // users; the per-item `canManage` flag drives the configuration
    // affordances downstream.
    //
    // Favorites use the dedicated `GET /list/favorites` endpoint for
    // the favorites-first load strategy (faster than filtering the full
    // /manage payload client-side). The per-item `isFavorite` flag on
    // /manage rows still drives the star icon in the main grid.
    // Groups → `list/groups` is the single source of truth for both
    // regular users (access-filtered list) and admins (full tenant
    // list). No `adminGroups` fallback to `customListGroup/paged` —
    // that route stays reserved for `/configuration/custom-list-groups`.
    routes: {
      allowed: 'list/manage',
      groups: 'list/groups',
      favorites: 'list/favorites',
    },
    queries: {
      // `canManage` gates configuration UI; `isFavorite` is only computed
      // when explicitly requested in `$select`.
      allowed: {
        ...ManageLists.paged,
        select: `${ManageLists.paged.select},canManage,isFavorite`,
      },
      // Merge the legacy `adminGroups` overrides (orderBy) on top of the
      // shared `ManageLists.groups` shape so tile chips/badges keep
      // rendering with the same fields, while still hitting the
      // access-aware `list/groups` endpoint.
      groups: {
        ...ManageLists.groups,
        orderBy: 'name asc',
      },
      // `canManage` drives the row-command kebab; `isFavorite` keeps the
      // star icon filled in the favorites rail.
      favorites: {
        ...ManageLists.favorites,
        select: `${ManageLists.favorites.select},canManage,isFavorite`,
      },
    },
    open: (item, e) => openInSameTabOrNew(`/custom-lists/view?listId=${item.id}`, e),
    getIconUrl: () => null,
    getMeta: getListMeta,
  },

  reports: {
    id: 'reports',
    labelKey: 'launchpad.type.reports',
    labelSingularKey: 'launchpad.type.reports.singular',
    accent: '#FF8D44',
    Icon: BarChartOutlined,
    source: 'launchpad',
    hasGroups: true,
    hasSubgroups: false,
    hasFavorites: true,
    nameField: 'title',
    groupIdField: 'reportGroupId',
    embeddedGroupField: 'reportGroup',
    groupParentField: null,
    // Per the "AccessControl vs Collaborator" UX guide, the Launchpad
    // is the main management view (left-sidebar `/reports` entry) →
    // uses `/report/manage`. Every other context (selectors, embedded
    // pickers, process action configs) keeps reading `/report/allowed`.
    // The per-item `canManage` flag tells the UI which records are
    // configurable.
    //
    // Favorites use the dedicated `GET /report/favorites` endpoint for
    // the favorites-first load strategy. The per-item `isFavorite`
    // flag on /manage rows still drives the star icon in the main grid.
    // Groups → `report/groups` is the single source of truth for both
    // regular users (access-filtered list) and admins (full tenant
    // list). No `adminGroups` fallback to `reportGroup/paged` —
    // that route stays reserved for `/configuration/reports`.
    routes: {
      allowed: 'report/manage',
      groups: 'report/groups',
      systemAllowed: 'report/adminReports',
      favorites: 'report/favorites',
    },
    queries: {
      // `canManage` gates configuration UI; `isFavorite` is only computed
      // when explicitly requested in `$select`.
      allowed: {
        ...ManageReports.reports,
        select: `${ManageReports.reports.select},canManage,isFavorite`,
      },
      systemAllowed: ManageReports.reports,
      // Merge the legacy `adminGroups` overrides on top of the shared
      // `ManageReports.groups` shape so tile chips/badges keep
      // rendering with the same fields, while still hitting the
      // access-aware `report/groups` endpoint.
      groups: {
        ...ManageReports.groups,
        select: 'id,name,isActive',
        searchSelect: ['name', 'description'],
        orderBy: 'name asc',
      },
      // `canManage` drives the row-command kebab; `isFavorite` keeps the
      // star icon filled in the favorites rail.
      favorites: {
        ...ManageReports.favorites,
        select: `${ManageReports.favorites.select},canManage,isFavorite`,
      },
    },
    open: (item, e) => {
      const groupId = item.reportGroupId || item.reportGroup?.id;
      if (!groupId) return;
      openInSameTabOrNew(`/reports/${groupId}/${item.id}`, e);
    },
    getIconUrl: () => null,
    getMeta: getReportMeta,
  },

  agents: {
    id: 'agents',
    labelKey: 'launchpad.type.agents',
    labelSingularKey: 'launchpad.type.agents.singular',
    accent: '#23C6C8',
    Icon: RobotOutlined,
    source: 'launchpad',
    // AIAgentTeam grouping. Same flat-one-level model as `connections`:
    // we expose a single `aiAgentTeamId` FK on each agent and a separate
    // `ai/agentTeam/paged` endpoint for the folders. No subgroups, no
    // per-user favorites for now.
    hasGroups: true,
    hasSubgroups: false,
    hasFavorites: false,
    nameField: 'name',
    groupIdField: 'aiAgentTeamId',
    embeddedGroupField: 'aiAgentTeam',
    groupParentField: null,
    // Per the "AccessControl vs Collaborator" UX guide, the Launchpad
    // is the main management view (left-sidebar `/agents` entry) → uses
    // `/ai/agent/manage`. Every other context (selectors, embedded
    // pickers, process action configs) keeps reading
    // `/ai/agent/allowed`. The backend returns the full set for admins
    // and the access-controlled subset for everyone else; the per-item
    // `canManage` flag tells the UI which records are configurable.
    routes: {
      allowed: 'ai/agent/manage',
      // Single source of truth for teams — same endpoint for both
      // regular users and admins. The backend doesn't yet expose a
      // separate access-aware variant, so the `groups` slot covers
      // every caller; no `adminGroups` fallback to keep the shape
      // aligned with `entity/groups`, `list/groups`, `report/groups`.
      groups: 'ai/agentTeam/paged',
    },
    queries: {
      allowed: {
        // `aiAgentTeamId` + expand `aiAgentTeam` so the tile can render
        // a "Team" chip without a second roundtrip. Same shape we use in
        // the agent builder (manageAgents.js). `canManage` drives the
        // gating of the "Configurar" action (admins + collaborators).
        select: 'id,name,isActive,description,iconUrl,provider,isPublic,aiAgentTeamId,canManage',
        searchSelect: ['name', 'description'],
        orderBy: 'name',
        expand: {
          aiAgentTeam: { select: 'id,name' },
        },
      },
      groups: {
        select: 'id,name,description,isActive',
        searchSelect: ['name', 'description'],
        orderBy: 'name asc',
      },
    },
    // `AIAgentTeam` carries `isActive` — keep the default behaviour
    // (hide inactive groups for users, surface them all for admins).
    // We don't flip `groupsHaveActiveFlag` so `normalizeGroup` respects
    // the column.
    open: (item, _e, ctx) => {
      if (!item?.id || !ctx?.dispatch) return;
      const { dispatch, formatMessage, message: notify } = ctx;
      const showError = () => {
        if (notify && formatMessage) {
          notify.error(formatMessage({ id: 'global.error.automyAI.openChat' }));
        }
      };

      try {
        dispatch({
          type: 'chat/changeAgent',
          payload: {
            agentId: item.id,
            chatType: CoreEnums.ChatType.GeneralSupport,
            onSuccess: (response) => {
              const threadId = response?.id;
              if (!threadId) {
                showError();
                return;
              }
              dispatch({
                type: 'chat/fetchMessages',
                payload: {
                  route: `chat/messages/${threadId}?$skip=0&$top=10&$orderBy=created%20desc&$filter=messageType ne Automy.Core.Enums.Chat.ChatMessageType'UserJoinChat'&$select=id,chatThreadId,messageType,status,handlerType,senderType,userId,content,userName,errorMessage,relatedMessageId,created,updated`,
                },
              });
              dispatch({
                type: 'chat/fetchOpenChat',
                payload: true,
              });
            },
            onError: showError,
          },
        });
      } catch (_error) {
        showError();
      }
    },
    getIconUrl: (item) => item?.iconUrl || null,
    getMeta: getAgentMeta,
  },

  // RPA: `launchpad.home.rpa` data; kebab still uses `agents` model with agentType rpa.
  rpa: {
    id: 'rpa',
    labelKey: 'launchpad.type.rpa',
    labelSingularKey: 'launchpad.type.rpa.singular',
    accent: '#7A5AF8',
    Icon: DesktopOutlined,
    source: 'launchpad',
    hasGroups: false,
    hasSubgroups: false,
    hasFavorites: false,
    nameField: 'name',
    groupIdField: null,
    embeddedGroupField: null,
    groupParentField: null,
    routes: {
      allowed: 'rpa/agent/paged',
      adminAllowed: 'rpa/agent/paged',
    },
    queries: {
      allowed: {
        select: 'id,name,isActive,deviceName,ip,agentPlatform,status',
        searchSelect: ['name', 'deviceName'],
        orderBy: 'name',
      },
      adminAllowed: {
        select: 'id,name,isActive,deviceName,ip,agentPlatform,status',
        searchSelect: ['name', 'deviceName'],
        orderBy: 'name',
      },
    },
    open: (item, e, ctx) => {
      if (!item?.id || !ctx?.dispatch) return;
      ctx.dispatch({
        type: 'agents/addNewAgent',
        payload: { state: true, item, view: true, id: item.id, agentType: 'rpa' },
      });
    },
    getDescription: (raw) => raw?.deviceName || raw?.ip || '',
    getIconUrl: () => null,
    getMeta: getRpaMeta,
  },

  // Skills tab: open/add via `aiSkills` model; data from `launchpad.home.skills`.
  // AISkillGroup grouping. Flat single-level (no subgroups): one
  // `aiSkillGroupId` FK on each skill + `ai/skillGroup/paged` endpoint
  // for the folders. Same shape as agents/connections — keeps the
  // Launchpad grid renderer happy without any tab-specific branches.
  skills: {
    id: 'skills',
    labelKey: 'launchpad.type.skills',
    labelSingularKey: 'launchpad.type.skills.singular',
    accent: '#F59E0B',
    Icon: ThunderboltOutlined,
    source: 'launchpad',
    hasGroups: true,
    hasSubgroups: false,
    hasFavorites: false,
    nameField: 'name',
    groupIdField: 'aiSkillGroupId',
    embeddedGroupField: 'aiSkillGroup',
    groupParentField: null,
    routes: {
      allowed: 'ai/skill/paged',
      adminAllowed: 'ai/skill/paged',
      // Same endpoint for both user and admin reads. Backend doesn't
      // gate skill groups per-user yet.
      groups: 'ai/skillGroup/paged',
      adminGroups: 'ai/skillGroup/paged',
    },
    queries: {
      allowed: {
        // `aiSkillGroupId` + expand `aiSkillGroup` so the tile can show
        // the folder name without a second request. Mirrors the agents
        // pattern (see `manageAgents.js` / agents queries above).
        select: 'id,name,description,isActive,packageName,published,sourceProvider,aiSkillGroupId',
        searchSelect: ['name', 'description'],
        orderBy: 'name',
        expand: {
          aiSkillGroup: { select: 'id,name' },
        },
      },
      adminAllowed: {
        select: 'id,name,description,isActive,packageName,published,sourceProvider,aiSkillGroupId',
        searchSelect: ['name', 'description'],
        orderBy: 'name',
        expand: {
          aiSkillGroup: { select: 'id,name' },
        },
      },
      groups: {
        select: 'id,name,description,isActive',
        searchSelect: ['name', 'description'],
        orderBy: 'name asc',
      },
      adminGroups: {
        select: 'id,name,description,isActive',
        searchSelect: ['name', 'description'],
        orderBy: 'name asc',
      },
    },
    open: (item, _e, ctx) => {
      if (!item?.id || !ctx?.dispatch) return;
      ctx.dispatch({
        type: 'aiSkills/showForm',
        payload: { state: true, id: item.id, item: null, detail: true },
      });
    },
    getIconUrl: () => null,
    getMeta: getSkillMeta,
  },

  apps: {
    id: 'apps',
    labelKey: 'launchpad.type.apps',
    labelSingularKey: 'launchpad.type.apps.singular',
    accent: '#F5B300',
    Icon: AppstoreOutlined,
    source: 'launchpad',
    hasGroups: false,
    hasSubgroups: false,
    hasFavorites: false,
    nameField: 'name',
    groupIdField: null,
    embeddedGroupField: null,
    groupParentField: null,
    // Per the "AccessControl vs Collaborator" UX guide, the Launchpad
    // is the main management view (left-sidebar `/apps` entry) → uses
    // `/app/manage`. Every other context (selectors, embedded pickers,
    // process action configs, access-log) keeps reading `/app/allowed`.
    // Per-item `canManage` gates the "Configurar" action on each tile.
    routes: {
      allowed: 'app/manage',
    },
    queries: {
      allowed: {
        select:
          'id,name,description,isActive,creator,modifier,created,updated,definition,canManage',
        searchSelect: ['name', 'description'],
        orderBy: 'updated desc',
      },
    },
    open: (item, e) => {
      if (!item?.id) return;
      openInSameTabOrNew(`/apps/${item.id}`, e);
    },
    getIconUrl: (item) => getAppIconUrl(item),
    getMeta: getAppMeta,
  },

  entities: {
    id: 'entities',
    labelKey: 'launchpad.type.entities',
    labelSingularKey: 'launchpad.type.entities.singular',
    accent: '#13C2C2',
    Icon: DatabaseOutlined,
    source: 'launchpad',
    hasGroups: true,
    hasSubgroups: false,
    hasFavorites: true,
    nameField: 'displayName',
    groupIdField: 'dynamicEntityGroupId',
    embeddedGroupField: 'dynamicEntityGroup',
    groupParentField: null,
    // Per the "AccessControl vs Collaborator" UX guide, the Launchpad
    // is the main management view (left-sidebar `/entities` entry) →
    // uses `/entity/manage`. Every other context (process action
    // configs, report-token forms, app-builder selectors, embedded
    // providers) keeps reading `/entity/allowed`. The backend filters
    // by access; `canManage` per item drives the configuration UI.
    //
    // Favorites use the dedicated `GET /entity/favorites` endpoint for
    // the favorites-first load strategy (faster than filtering the full
    // /manage payload client-side). The per-item `isFavorite` flag on
    // /manage rows still drives the star icon in the main grid.
    // Groups → `entity/groups` is now the single source of truth for
    // both regular users (access-filtered list) and admins (full
    // tenant list). No `adminGroups` fallback to the admin CRUD
    // endpoint `dynamicEntityGroup/paged` — that route stays reserved
    // for `/configuration/dynamic-entity-groups`.
    routes: {
      allowed: 'entity/manage',
      groups: 'entity/groups',
      favorites: 'entity/favorites',
    },
    queries: {
      // `canManage` gates configuration UI; `isFavorite` is only computed
      // when explicitly requested in `$select`.
      allowed: {
        ...ManageEntities.Entities,
        select: `${ManageEntities.Entities.select},canManage,isFavorite`,
      },
      // Match the legacy `adminGroups` shape (select + orderBy) so the
      // tile chips and badges keep rendering with the same fields,
      // while still hitting the access-aware `entity/groups` endpoint.
      groups: {
        ...ManageEntities.Groups,
        select: 'id,name,description,isActive',
        orderBy: 'name asc',
      },
      // `canManage` drives the row-command kebab; `isFavorite` keeps the
      // star icon filled in the favorites rail.
      favorites: {
        ...ManageEntities.Favorites,
        select: `${ManageEntities.Favorites.select},canManage,isFavorite`,
      },
    },
    open: (item, e) => {
      if (!item?.id) return;
      openInSameTabOrNew(`/entities/${item.id}`, e);
    },
    getIconUrl: () => null,
    // Reuse the shared meta helper so `canManage` is normalised on the
    // tile (same as agents/lists/apps).
    getMeta: (raw) => ({ canManage: raw?.canManage === true }),
  },

  connections: {
    id: 'connections',
    labelKey: 'launchpad.type.connections',
    labelSingularKey: 'launchpad.type.connections.singular',
    accent: '#296DFF',
    Icon: ApiOutlined,
    source: 'launchpad',
    hasGroups: true,
    hasSubgroups: false,
    hasFavorites: false,
    nameField: 'name',
    groupIdField: 'groupId',
    embeddedGroupField: 'group',
    groupParentField: null,
    // Per the "AccessControl vs Collaborator" UX guide, the Launchpad
    // is the main management view (left-sidebar `/connections` entry)
    // → uses `/connection/manage`. Every other context (selectors,
    // pickers, process action configs) keeps reading `/connection/allowed`.
    // The per-item `canManage` flag tells the UI which records are
    // configurable. Connections do not expose per-user favorites.
    // Groups → `connection/groups` is the single source of truth for
    // both regular users (access-filtered list) and admins (full tenant
    // list). No `adminGroups` fallback to `connectionGroup/paged` —
    // that route stays reserved for the legacy `/connections/groups`
    // CRUD page.
    routes: {
      allowed: 'connection/manage',
      groups: 'connection/groups',
    },
    queries: {
      // `canManage` gates configuration UI on each tile.
      allowed: {
        ...(ManageConnections.paged || {}),
        select: 'id,type,name,description,isActive,validated,isPublic,groupId,canManage',
      },
      groups: {
        select: 'id,name,description',
        searchSelect: ['name', 'description'],
        orderBy: 'name asc',
      },
    },
    groupsHaveActiveFlag: false,
    open: () => {},
    getIconUrl: () => null,
    getIconNode: getConnectionIconNode,
    getMeta: getConnectionMeta,
  },
};

export const ENTITY_TYPE_ORDER = [
  'processes',
  'lists',
  'reports',
  'agents',
  'rpa',
  'skills',
  'apps',
  'connections',
  'entities',
];

export const getEntityType = (id) => ENTITY_TYPES[id] || ENTITY_TYPES.processes;

// Tile shape + `raw` for favorites/commands; merges optional `getMeta` spread.
export const normalizeItem = (raw, typeConfig) => {
  if (!raw) return null;
  const type = typeConfig || ENTITY_TYPES.processes;
  const name = raw[type.nameField] || raw.name || raw.title || '';
  const embedded = type.embeddedGroupField ? raw[type.embeddedGroupField] : null;
  const groupId = type.groupIdField
    ? raw[type.groupIdField] || embedded?.id || null
    : embedded?.id || null;
  const iconUrl = type.getIconUrl ? type.getIconUrl(raw) : null;
  const iconNode = type.getIconNode ? type.getIconNode(raw) : null;
  const description = type.getDescription
    ? type.getDescription(raw) || ''
    : raw.description || raw.longDescription || '';
  return {
    id: raw.id,
    name,
    description,
    groupId,
    groupName: embedded?.name || null,
    isFavorite: !!raw.isFavorite,
    isActive: raw.isActive === true,
    startMode: raw.startMode ?? null,
    isBulkRequestCreationEnabled: raw.isBulkRequestCreationEnabled === true,
    isSystemReport: raw.isSystemReport === true,
    isPremiumEntity: raw.isPremiumEntity === true,
    iconNode: iconNode || null,
    iconUrl: iconUrl || null,
    type: type.id,
    ...(type.getMeta ? type.getMeta(raw) : {}),
    raw,
  };
};

export const normalizeGroup = (raw, typeConfig) => {
  if (!raw) return null;
  const type = typeConfig || ENTITY_TYPES.processes;
  return {
    id: raw.id,
    name: raw.name || '',
    parentId: type.groupParentField ? raw[type.groupParentField] || null : null,
    isActive: type.groupsHaveActiveFlag === false ? true : raw.isActive === true,
    itemsCount:
      raw.processesCount ||
      raw.customListsCount ||
      raw.reportsCount ||
      raw.dynamicEntitiesCount ||
      // `AIAgentTeam` backend exposes the inverse navigation as `aiAgents`
      // (the OData `$expand=aiAgents($count=true)` would surface this).
      // Kept here as a soft fallback — if the API doesn't ship the count,
      // `useLaunchpadData.populatedGroups` still infers populated state
      // from `loadedCount` once items finish paginating.
      raw.aiAgentsCount ||
      // `AISkillGroup`'s inverse navigation is `aiSkills`. Same soft
      // fallback story — present only when the backend includes it.
      raw.aiSkillsCount ||
      raw[`${type.id}Count`] ||
      0,
    groupsCount: raw.groupsCount || 0,
    raw,
  };
};
